import { z } from 'zod';
import { ADMIN_PASSWORD_MINIMUM, INVITE_VALID_HOURS } from './lists.ts';
import { residenceCountry } from './residency.ts';

/**
 * THE PLAYER REALM — registration, sign-in, and confirming an address.
 *
 * Its own file rather than more of `auth.ts`, and the reason is not tidiness.
 * `auth.ts` describes a STAFF sign-in: a work email, a mandatory second factor
 * for anyone who can move money, a trusted-device option, a challenge token.
 * None of that applies here, and several of them must NOT be reachable from
 * here. Sharing the file is how a player request acquires a `trustDevice` field
 * that some handler eventually honours.
 *
 * WHAT IS DELIBERATELY ABSENT FROM THIS FILE, so nobody reads it as incomplete:
 *
 *  · A `brandId`. Brand is a TENANCY BOUNDARY, and a client-supplied tenancy
 *    boundary is not a boundary. It is resolved server-side from the request
 *    host against `platform.brand.domain`, which carries a unique index, and
 *    there is no field here a caller could use to override that. This is the
 *    single most important thing this file does not contain.
 *
 *  · Nothing about a password reset is absent any more, and this note used to
 *    say it was. Self-service reset WAS refused on the ground that half a
 *    credential-recovery path is worse than none while the platform could not
 *    send an email; `packages/mailer` closed that, so the shapes are at the
 *    foot of this file and the login screen carries a working link rather than
 *    a sentence explaining an absence. What is still deliberately absent from
 *    the RESULT shapes is everything that would say whether an address is
 *    registered — see the note on `requestPlayerPasswordResetResult`.
 *
 *  · Anything about a second factor AT SIGN-IN. Player sign-in verifies none,
 *    by decision, and `playerSignInRequest` below carries no field that could
 *    change that. The step-up belongs on WITHDRAWAL, where the money is.
 *
 *    THAT STEP-UP NOW EXISTS. This note used to end by saying the withdrawal
 *    route verified nothing either, "because a player account has no second
 *    factor to verify" — both halves have stopped being true. A player may
 *    enrol an optional authenticator (`./player-two-factor.ts`), and
 *    `requestWithdrawalRequest` in `./player-money.ts` carries a `stepUp` the
 *    route verifies inside the withdrawal's own transaction. None of those
 *    shapes is in THIS file, and that is the boundary it keeps: nothing here
 *    is part of getting in.
 */

/* ── The floors ────────────────────────────────────────────────────────────── */

/**
 * The same password floor as an operator and a partner, and for the same
 * mechanical reason: `hashPassword` in @dayon/auth throws below twelve
 * characters, so a lower minimum here is a form that accepts what the hasher
 * then refuses — a 500 on the happy path.
 *
 * There is a product argument for a lower floor on a consumer product, and it
 * is refused: this account holds withdrawable money, and the population using
 * it is the one an attacker actually targets with a credential list.
 */
export const PLAYER_PASSWORD_MINIMUM = ADMIN_PASSWORD_MINIMUM;

/**
 * How long a verification link lives, in hours.
 *
 * The same 72 as both invites. Re-exported rather than re-declared, so the
 * platform has ONE answer to "how long does a link last" and the three cannot
 * be changed apart.
 */
export const PLAYER_VERIFICATION_VALID_HOURS = INVITE_VALID_HOURS;

/** A handle, not an identity. Sign-in is by email; this is what a screen shows. */
export const playerDisplayName = z
  .string()
  .trim()
  .min(3, 'Choose at least 3 characters.')
  .max(40, 'Keep it under 40 characters.')
  /*
   * Letters, digits, spaces and a few separators. NOT a general string.
   *
   * This value is rendered into an HTML email delivered from the operator's own
   * DKIM-signed domain, and into every screen that greets the player. The
   * templates escape it and the tests assert they do — this is the second layer,
   * and it is here because the first one only has to be forgotten once. It also
   * refuses the homoglyph-and-zero-width tricks used to impersonate a support
   * account in a chat window.
   */
  .regex(
    /^[\p{L}\p{N}][\p{L}\p{N} ._'-]*$/u,
    'Use letters, numbers, spaces, and . _ - or apostrophe.',
  );

/**
 * THE TIDY STEP, applied to a part of a name before any rule reads it, so the
 * same name typed two ways is stored one way. The identity check compares
 * strings, and the console sets the name beside the ID's reading: a second
 * spelling of one name would be reported as a different name. In this order:
 *
 *   1. NORMALISED (Unicode NFKC). An accent typed as a separate mark is joined to
 *      its letter — "n" and a combining tilde are stored as the one letter ñ —
 *      and letters drawn wide or in a mathematical style become plain ones: a
 *      fullwidth or a bold "Juan" is "Juan". One character is changed just
 *      before: the acute accent typed on its own (´, U+00B4), which normalising
 *      would turn into a space and an accent. It is typed for an apostrophe, and
 *      it is made one.
 *   2. EVERY APOSTROPHE MADE A PLAIN ONE: ’ (U+2019, which iOS swaps in as the
 *      player types), ‘ (U+2018, which it types at the start of a word), the
 *      modifier letter apostrophe (U+02BC), the backtick, and ´.
 *   3. EVERY HYPHEN AND DASH MADE A PLAIN HYPHEN: U+2010 to U+2015, among them the
 *      em dash iOS makes out of a double hyphen.
 *   4. The ends trimmed, and a run of spaces inside closed up to one.
 *
 * Steps 2 and 3 come after step 1 so they also see what normalising produces: a
 * small em dash (U+FE58) becomes an em dash, and then a hyphen.
 */
function tidyNamePart(raw: string): string {
  return raw
    .replace(/\u{B4}/gu, "'")
    .normalize('NFKC')
    .replace(/[\u{2018}\u{2019}\u{2BC}\u{60}]/gu, "'")
    .replace(/[\u{2010}-\u{2015}]/gu, '-')
    .replace(/\s+/g, ' ')
    .trim();
}

/** The alphabets whose letters look alike: one part of a name may use one of them, not two. */
const LOOKALIKE_ALPHABETS = [/\p{Script=Latin}/u, /\p{Script=Greek}/u, /\p{Script=Cyrillic}/u];

/** What a box for a part of the name says when it holds no name: empty, spaces, or no letter at all. */
const ENTER_THIS_PART = 'Enter this part of your name.';

/**
 * WHAT A NAME RULE SAYS, IN THE WORDS OF THE WAY THE NAME IS GIVEN: as a part of a name (first,
 * middle or last), or as the one name of an ID that shows only one. The one Name box holds the whole
 * name, so "this part of your name" under it contradicts the player, who has just ticked "My ID
 * shows only one name". The other sentences of the rule ("Keep it to 60 characters.", "Use letters
 * only…", and the two about accents, which are about a letter) are true under either box, and are
 * shared.
 */
interface NameRuleWords {
  /** The box holds no name: empty, spaces, or no letter at all. */
  enter: string;
  /** A character that is drawn as nothing is in it. */
  hidden: string;
  /** Letters of two alphabets that look alike are mixed in it. */
  oneAlphabet: string;
}

const PART_OF_A_NAME: NameRuleWords = {
  enter: ENTER_THIS_PART,
  hidden: 'Type this part of your name again — it has a hidden character in it.',
  oneAlphabet: 'Use one alphabet for each part of your name.',
};

const THE_ONE_NAME: NameRuleWords = {
  enter: 'Enter your name as your ID shows it.',
  hidden: 'Type your name again — it has a hidden character in it.',
  oneAlphabet: 'Use one alphabet for your name.',
};

/** What "I have no middle name" is told when it arrives neither ticked nor unticked. */
const NO_MIDDLE_NAME_TICK = 'Tick “I have no middle name”, or leave it unticked.';

/**
 * ONE PART OF THE PLAYER'S NAME, AS THEIR ID PRINTS IT — first, middle or last.
 *
 * Not a handle. The display name above is what a screen calls the player; this
 * is what the identity check compares with the document, so the rule has two
 * jobs that pull against each other: accept every real name EXACTLY — "Ma.
 * Angelica", "Dela Cruz", "Peñaflor", "O'Neil", "Ong-Villanueva", a two-letter
 * surname such as "Go" — and refuse what no ID prints: digits and markup.
 *
 * Tidied first (`tidyNamePart`, above). Then letters of any alphabet, accents
 * typed as part of the letter or as a separate mark, and spaces, periods,
 * apostrophes and hyphens, in any order: "'t Hooft" is kept as written. Sixty
 * characters, counted after tidying, which is also the column
 * (`player.declared_name`, sql/1018). Then the refusals the list of characters
 * cannot make on its own:
 *
 *   · NOTHING THAT IS DRAWN AS NOTHING. Unicode counts some invisible characters
 *     as letters or accents: the Hangul fillers (U+3164 and its kin), the
 *     combining grapheme joiner (U+034F), the variation selectors. A part made
 *     of a filler is a legal name, written once, that looks blank on every
 *     screen; "Juan" with a joiner after it looks like "Juan" and is another
 *     string, the two-spellings problem the tidy step exists to prevent.
 *     Unicode's own list of characters drawn as nothing
 *     (Default_Ignorable_Code_Point) holds none of ñ, a separate accent or a
 *     letter of any alphabet. It is checked BEFORE the list of characters, so a
 *     zero-width space pasted after "Juan" is called hidden, not a symbol the
 *     player cannot find.
 *   · EVERY ACCENT ON A LETTER, AND NO MORE THAN THREE ON ONE. A combining mark
 *     first, or straight after a space or punctuation, sits on no letter. No
 *     alphabet writes a fourth mark on one letter: "A" with 59 acute accents
 *     piled on it fitted in sixty characters and was kept.
 *   · ONE ALPHABET TO A PART. Latin, Greek and Cyrillic share letters that look
 *     the same and are different characters: "Maria" with a Cyrillic a (U+0430)
 *     in it looks like "Maria" and is another string. A part written all in one
 *     alphabet is kept, whichever alphabet it is.
 *   · AT LEAST ONE LETTER. "-" or "." alone, which people write on paper for
 *     "none", holds only allowed characters and is still no name, so it is asked
 *     for as an empty box is. The middle-name box reads it as nothing typed
 *     (`holdsNoName`, below), so the sentence under that box says how to say
 *     "none".
 *
 * The first and the last name are asked one thing more: no suffix typed after
 * them (`playerFirstOrLastName`, below). The one name of an ID that shows only
 * one is asked the same rules in words about the whole name (`playerOneName`).
 *
 * Each sentence is true of exactly what it refuses; a form shows the first one.
 */
export const playerNamePart = namePartRule({ refuseTypedSuffix: false, words: PART_OF_A_NAME });

/**
 * A SUFFIX TYPED INTO THE FIRST OR LAST NAME: Jr., Sr., II, III or IV as the
 * last word of the box, after a space or a comma — "Dela Cruz Jr.", "Dela Cruz,
 * Jr." (how a driver's licence prints it), "Jose III" — in any case, with or
 * without its period. Read after the tidy step, so the Roman numeral three typed
 * as one character reads as III.
 *
 * Only a whole word, and only after a name: Junior, Ivy and Srinivas are names,
 * and "Ii" alone is a Japanese surname. Not V: a lone V after a name is usually
 * an initial ("Juan V").
 */
const SUFFIX_TYPED_IN_A_NAME = /[ ,](?:jr|sr|ii|iii|iv)\.?$/iu;

/**
 * THE FIRST OR THE LAST NAME: a part of the name (`playerNamePart`, above) that
 * also refuses a suffix typed after it (`SUFFIX_TYPED_IN_A_NAME`).
 *
 * Those are the two boxes a suffix gets typed into: after the surname, as an ID
 * prints the whole name, or after the first name. Kept, "Dela Cruz Jr." with
 * Suffix at None is a name split one way here and another way on the ID, and
 * the identity check sends it to a person. The sentence says where the suffix
 * goes, and it comes before the list of characters, so "Dela Cruz, Jr." is
 * told that rather than "letters only" for its comma.
 */
export const playerFirstOrLastName = namePartRule({
  refuseTypedSuffix: true,
  words: PART_OF_A_NAME,
});

/**
 * THE ONE NAME OF AN ID THAT SHOWS ONLY ONE: the rules of a part of a name
 * (`playerNamePart`, above), said in words about the whole name — "Enter your
 * name as your ID shows it." where an empty part box says "Enter this part of
 * your name." (`THE_ONE_NAME`, above).
 *
 * No suffix is looked for after it. With "My ID shows only one name" ticked the
 * form shows no Suffix box to point to, and a one-name person has no other way
 * to give a suffix their ID prints, so "Sukarno Jr." is kept as written; a
 * difference from the ID goes to a person.
 */
export const playerOneName = namePartRule({ refuseTypedSuffix: false, words: THE_ONE_NAME });

/** The rule for one part of the name, in the order its sentences are said; see `playerNamePart`. */
function namePartRule({
  refuseTypedSuffix,
  words,
}: {
  refuseTypedSuffix: boolean;
  words: NameRuleWords;
}) {
  const sized = z
    .string(words.enter)
    .overwrite(tidyNamePart)
    .min(1, words.enter)
    .max(60, 'Keep it to 60 characters.')
    .refine((part) => !/\p{Default_Ignorable_Code_Point}/u.test(part), words.hidden);
  const suffixAsked = refuseTypedSuffix
    ? sized.refine(
        (part) => !SUFFIX_TYPED_IN_A_NAME.test(part),
        'Choose Jr., Sr., II, III or IV in Suffix instead of typing it in your name.',
      )
    : sized;
  return suffixAsked
    .regex(
      /^[\p{L}\p{M} .'-]*$/u,
      'Use letters only, with spaces, periods, apostrophes or hyphens — no numbers or symbols.',
    )
    .refine((part) => !/(?:^|[ .'-])\p{M}/u.test(part), 'Put each accent on a letter.')
    .refine((part) => !/\p{M}{4}/u.test(part), 'Use no more than three accents on one letter.')
    .refine(
      (part) => LOOKALIKE_ALPHABETS.filter((alphabet) => alphabet.test(part)).length < 2,
      words.oneAlphabet,
    )
    .regex(/\p{L}/u, words.enter);
}

/**
 * THE WORDS PEOPLE WRITE IN A BOX FOR "NONE", which no ID prints as a name: N/A
 * (n/a, "N / A"), N.A., None, NMN ("no middle name"), and the two they stand
 * for, "No middle name" and "Not applicable". Read whole, in any case, with or
 * without a period after them.
 *
 * Not "NA" or "Na": Na is a Korean surname, which an ID printed in capitals
 * shows as NA, and a Filipino's middle name is the mother's maiden surname. Not
 * "Wala" either, though it is Filipino for "none": it is a surname too. Nor
 * "Null" or "Nil", which are names. A word that is somebody's name stays a
 * name, because reading it as "none" leaves that person no way to give their
 * own middle name.
 */
const WRITTEN_FOR_NONE =
  /^(?:n ?\/ ?a|n\. ?a|none|n\.? ?m\.? ?n|no middle name|not applicable)\.?$/iu;

/**
 * A NAME BOX THAT HOLDS NO NAME: empty, spaces, only the "-" or "." (or a lone
 * apostrophe) people write on a paper form for "none", or one of the words they
 * write for it (`WRITTEN_FOR_NONE`, above) — read after the same tidying as a
 * name, so the dash or the curly quote a phone puts in place of "-" or "'" holds
 * no name either, and nor does an N/A typed on a wide keyboard.
 *
 * The middle-name box reads it as nothing typed. Without the tick the player is
 * asked "Enter your middle name, or tick “I have no middle name”.", the sentence
 * that says how to say "none"; beside the tick it IS the tick, so a "-" or a
 * "None" is never kept as anybody's middle name. Exported so the sign-up form
 * checks exactly this before it sends.
 */
export const holdsNoName = (box: string): boolean => {
  const tidied = tidyNamePart(box);
  return /^[ .'-]*$/u.test(tidied) || WRITTEN_FOR_NONE.test(tidied);
};

/**
 * The generational suffixes a player may choose, each with what a screen shows.
 *
 * The KEY is what is stored (`player.declared_name.suffix`, whose CHECK names the
 * same six) and what the identity check is sent. "None" is not on the list: it is
 * stored as no suffix at all.
 *
 * Not `NAME_SUFFIXES` in player-identity.ts, which leaves "V" out on purpose —
 * that list GUESSES a suffix out of free text, where a lone V is usually a middle
 * initial. This one is a choice the player made, so V is simply V.
 */
export const DECLARED_NAME_SUFFIXES = [
  { key: 'JR', label: 'Jr.' },
  { key: 'SR', label: 'Sr.' },
  { key: 'II', label: 'II' },
  { key: 'III', label: 'III' },
  { key: 'IV', label: 'IV' },
  { key: 'V', label: 'V' },
] as const;
export type DeclaredNameSuffix = (typeof DECLARED_NAME_SUFFIXES)[number]['key'];

const declaredNameSuffix = z.enum(
  DECLARED_NAME_SUFFIXES.map((s) => s.key) as [DeclaredNameSuffix, ...DeclaredNameSuffix[]],
  'Choose a suffix from the list, or None.',
);

/**
 * "NOTHING CHOSEN", HOWEVER A FORM SAYS IT, for the name answers that may be left out.
 *
 * An answer left empty can arrive as no field at all, as `''` (an empty box, or a
 * "None" option whose value is empty) or as `null` (how the table and
 * `DeclaredNameParts` say "none"). All three are absent, for the middle name and
 * the suffix alike: a player who chose "None" is never told to choose a suffix
 * "or None", and a middle name sent as `null` beside the tick is the tick. What
 * else reads as an empty box is each answer's own: for the middle name, a box
 * that holds no name (`holdsNoName`); for the suffix, the empty value alone; for
 * the first name, a box of nothing but spaces. The first name may be left out
 * only beside "My ID shows only one name"; otherwise the registration asks for it.
 */
const nothingChosen =
  (holdsNothing: (box: string) => boolean) =>
  (value: unknown): unknown =>
    value === null || (typeof value === 'string' && holdsNothing(value)) ? undefined : value;

/** The four parts as `player.declared_name` holds them. */
export interface DeclaredNameParts {
  /** NULL: the player's ID shows only one name, which is `lastName` (the row's `single_name`). */
  firstName: string | null;
  /** NULL: the player ticked "I have no middle name", or their ID shows only one name. */
  middleName: string | null;
  lastName: string;
  /** A key from `DECLARED_NAME_SUFFIXES`; NULL for none. */
  suffix: string | null;
}

/**
 * The declared name written out for a screen: "Segundo Pascua Mercado Jr.", or
 * "Sukarno" alone for a player whose ID shows only one name.
 *
 * The suffix is shown by its LABEL, never its key. A key this does not know —
 * which the table's CHECK makes impossible — is shown as stored rather than
 * dropped, so a screen never hides part of a name.
 */
export function formatDeclaredName(parts: DeclaredNameParts): string {
  const suffix =
    parts.suffix === null
      ? null
      : (DECLARED_NAME_SUFFIXES.find((s) => s.key === parts.suffix)?.label ?? parts.suffix);
  return [parts.firstName, parts.middleName, parts.lastName, suffix]
    .filter((p): p is string => p !== null && p !== '')
    .join(' ');
}

/**
 * The declared name as STAFF read it beside the ID: `formatDeclaredName`, and for a player
 * whose ID shows only one name, that said in words — "Sukarno (one name only)" — so a
 * reviewer reading one word does not take it for a name cut short.
 *
 * One function for every console screen that shows it (the identity case, the player
 * page), so the two cannot write a one-name player differently. A missing first name is how
 * a one-name row is told apart: the table holds no first name on such a row
 * (`declared_name_single_name_is_one_part`) and a first name on every other
 * (`declared_name_first_is_not_blank`), the same reading the identity check's start job
 * makes before it sends the name to CIRA.
 */
export function formatDeclaredNameForStaff(parts: DeclaredNameParts): string {
  const name = formatDeclaredName(parts);
  return parts.firstName === null ? `${name} (one name only)` : name;
}

/**
 * A calendar date, as `YYYY-MM-DD`.
 *
 * A STRING and not a `z.date()`, deliberately. A date of birth is a calendar
 * date rather than an instant, and accepting an ISO datetime would let the
 * client's timezone decide which day it was — which on a birthday is the whole
 * question. The server turns this into an instant on the PLATFORM's calendar,
 * with `gamingDayInstant` in @dayon/db, so there is one conversion and it
 * happens where the timezone is known.
 */
/**
 * A PHILIPPINE MOBILE NUMBER, normalised to one form.
 *
 * PAGCOR item 21 field 3 is "Contact Information", and until now that was an
 * email address: `player.player.phone` has existed since the schema was drawn and
 * nothing had ever written to it.
 *
 * ── ACCEPTS THE THREE WAYS PEOPLE WRITE IT, STORES ONE ────────────────────
 *
 *   09171234567        how it is written on a form
 *   +639171234567      how it is written for international dialling
 *   639171234567       how it arrives from some providers
 *
 * All three are the same number and all three are stored as `+639171234567`.
 * Without normalisation the same person registering twice produces two different
 * strings, and `risk.account_link` — which compares contact details to spot one
 * person holding several accounts — would see two people.
 *
 * Spaces, dashes and brackets are stripped before matching, because a form field
 * gets all of them and none of them change the number.
 *
 * MOBILE ONLY, and that is deliberate rather than a limitation: a landline
 * cannot receive the one-time code item 14 names as a verification method, so
 * accepting one would record a contact number that cannot do the job the field
 * exists for.
 */
export const playerPhone = z
  .string()
  .transform((raw) => raw.replace(/[\s()\-.]/g, ''))
  .refine((v) => /^(?:\+63|63|0)9\d{9}$/.test(v), {
    message: 'Enter a Philippine mobile number, like 0917 123 4567.',
  })
  .transform((v) => `+63${v.replace(/^(?:\+63|63|0)/, '')}`);

export const calendarDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use the date picker, or type it as YYYY-MM-DD.');

/* ── Registration ──────────────────────────────────────────────────────────── */

/**
 * THE NAME AS THE ID PRINTS IT: the six answers of the name group, in the four parts the identity
 * check asks for, or as the one name an ID shows when it shows only one.
 *
 * Asked by sign-up (`registerPlayerRequest`, below) and asked again, by the same rules, when a player
 * corrects the name after a refusal because the details did not match
 * (`declaredDetailsCorrectionRequest`, player-identity.ts). The fields here, and the rules between
 * them in `askDeclaredName` below: one definition, so the two cannot ask a name differently.
 *
 * The display name (`playerDisplayName`) stays: it is the nickname agents,
 * affiliates and emails show. These are kept apart from it — at sign-up in
 * `player.declared_name` (sql/1018), a correction in
 * `player.declared_details_correction` (sql/1023), which nobody but the platform
 * itself can read — and they never go into the audit row.
 *
 * A middle name is required unless the player ticks "I have no middle name" —
 * exactly one of the two, which the refinement below enforces. An empty
 * middle-name box, one sent as `null`, or one holding only the "-" or "."
 * people write on paper for "none", or a word they write for it such as N/A or
 * None (`holdsNoName`), is no middle name typed, never "none": only the tick
 * says that, and without it the refinement asks for one. The tick is ticked or
 * not, and anything else sent for it is told so. A suffix is optional; "None"
 * may be sent as no `nameSuffix`, as `''` or as `null` (`nothingChosen`). A
 * suffix typed after the first or last name is refused with a sentence that
 * says to choose it in Suffix (`playerFirstOrLastName`).
 *
 * ONE NAME (the owner's decisions of 17 and 18 October 2026). With `singleName`
 * true, "My ID shows only one name", the one name is `lastName`, asked by
 * `playerOneName`: the rules of a part of a name, in words about the whole name,
 * and a suffix typed after it is kept as written, because the form shows no
 * Suffix box beside it to point to. The first name, the middle name, "I have no
 * middle name" and the suffix are then absent; each sent empty or as `null` is
 * absent too, and anything else beside the tick is refused against its own box.
 * Whoever ticks it, wherever they live: a resident of the Philippines may hold a
 * foreign passport that shows one name. With the tick off or not sent, nothing
 * changes.
 *
 * WHY THE FIRST AND LAST NAME ARE ASKED BY THE REFINEMENT AND NOT ON THEIR OWN
 * BOXES. Which rule asks the last name depends on the tick, and a box's own rule
 * cannot see the tick; the first name may be left out beside it. So these two
 * boxes only tidy what was typed (`tidyNamePart`), and the refinement asks each
 * with the rule for the way the name is given, every sentence in that rule's
 * order, under that box. A last name not sent, or sent as anything but text, is
 * an empty box, so it is asked for in those same words: "Enter your name as
 * your ID shows it." beside the tick, never "this part". Like every rule in a
 * refinement, they are asked once each answer of the request has the right type:
 * at sign-up, a missing email or a declaration sent as false is said first. The
 * sign-up form and the correction form ask the same rules before they send
 * anything.
 */
export const declaredNameFields = {
  singleName: z.boolean('Tick “My ID shows only one name”, or leave it unticked.').optional(),
  firstName: z.preprocess(
    nothingChosen((box) => tidyNamePart(box) === ''),
    z.string(ENTER_THIS_PART).overwrite(tidyNamePart).optional(),
  ),
  middleName: z.preprocess(nothingChosen(holdsNoName), playerNamePart.optional()),
  /* `''` is read as `null`, and `null` is let through here: beside "My ID shows
     only one name", where this tick is not on the form, either is the tick left
     unticked; while the name is in parts the refinement refuses both with this
     same sentence, as it refuses anything else that is neither ticked nor
     unticked. */
  noMiddleName: z.preprocess(
    (value) => (value === '' ? null : value),
    z.boolean(NO_MIDDLE_NAME_TICK).nullable().optional(),
  ),
  /* Text, tidied; not sent, or sent as anything but text, it is an empty box,
     which the refinement asks for in the words of the way the name is given. */
  lastName: z.preprocess(
    (value) => (typeof value === 'string' ? value : ''),
    z.string().overwrite(tidyNamePart),
  ),
  nameSuffix: z.preprocess(
    nothingChosen((box) => box === ''),
    declaredNameSuffix.optional(),
  ),
};

/** The name group's answers once each has its type: what `askDeclaredName` asks between them. */
export interface DeclaredNameAnswers {
  singleName?: boolean | undefined;
  firstName?: string | undefined;
  middleName?: string | undefined;
  noMiddleName?: boolean | null | undefined;
  lastName: string;
  nameSuffix?: DeclaredNameSuffix | undefined;
}

/**
 * THE RULES BETWEEN THE BOXES OF THE NAME, said under each box: the first and the last name by the
 * rule for the way the name is given, a middle name or its tick, and nothing else of the name beside
 * "My ID shows only one name". The second half of `declaredNameFields`, run as a refinement by every
 * request that carries the name group.
 */
export function askDeclaredName(body: DeclaredNameAnswers, ctx: z.RefinementCtx): void {
  /*
   * THE NAME: IN PARTS, OR AS THE ONE NAME THE ID SHOWS.
   *
   * Where the player lives decides nothing here (the owner's decision of
   * 18 October 2026): "My ID shows only one name" is taken from anybody, a
   * resident of the Philippines too, who may hold a foreign passport that shows
   * one name.
   */
  const oneNameTicked = body.singleName === true;
  /* Every sentence a rule has for one box of the name, under that box, in the
     rule's own order: what the box would say if the rule sat on it. Each once:
     an empty box is both empty and without a letter, and is asked for once. */
  const ask = (rule: typeof playerNamePart, path: 'firstName' | 'lastName', part: string) => {
    const said = new Set<string>();
    for (const issue of rule.safeParse(part).error?.issues ?? []) {
      if (said.has(issue.message)) continue;
      said.add(issue.message);
      ctx.addIssue({ code: 'custom', path: [path], message: issue.message });
    }
  };

  if (oneNameTicked) {
    /* The one name, by the rule in words about the whole name; and no other
       part beside it, each refused against its own box. Empty, `null` and the
       words for "none" in the middle-name box arrive here as absent already
       (`nothingChosen`), and "I have no middle name" sent as `''` or `null`
       is not ticked. */
    ask(playerOneName, 'lastName', body.lastName);
    for (const [given, path, message] of [
      [
        body.firstName !== undefined,
        'firstName',
        'Leave the first name empty when you tick “My ID shows only one name”.',
      ],
      [
        body.middleName !== undefined,
        'middleName',
        'Leave the middle name empty when you tick “My ID shows only one name”.',
      ],
      [
        body.noMiddleName === true,
        'noMiddleName',
        'Leave “I have no middle name” unticked when you tick “My ID shows only one name”.',
      ],
      [
        body.nameSuffix !== undefined,
        'nameSuffix',
        'Leave the suffix at None when you tick “My ID shows only one name”.',
      ],
    ] as const) {
      if (given) ctx.addIssue({ code: 'custom', path: [path], message });
    }
  } else {
    /* IN PARTS: each box asked exactly what it was asked before the tick existed. */
    if (body.firstName === undefined) {
      ctx.addIssue({ code: 'custom', path: ['firstName'], message: ENTER_THIS_PART });
    } else {
      ask(playerFirstOrLastName, 'firstName', body.firstName);
    }
    ask(playerFirstOrLastName, 'lastName', body.lastName);
    if (body.noMiddleName === null) {
      ctx.addIssue({ code: 'custom', path: ['noMiddleName'], message: NO_MIDDLE_NAME_TICK });
    }

    /*
     * A MIDDLE NAME, OR THE TICK — EXACTLY ONE.
     *
     * Neither leaves the platform unable to tell a player with no middle name from
     * one who skipped the box, and the identity check would compare a name that is
     * short a part. Both is a contradiction. Either way the message goes on
     * `middleName`, so the form shows it under that box.
     */
    const ticked = body.noMiddleName === true;
    if (body.middleName === undefined && !ticked) {
      ctx.addIssue({
        code: 'custom',
        path: ['middleName'],
        message: 'Enter your middle name, or tick “I have no middle name”.',
      });
    }
    if (body.middleName !== undefined && ticked) {
      ctx.addIssue({
        code: 'custom',
        path: ['middleName'],
        message: 'Leave the middle name empty when you tick “I have no middle name”.',
      });
    }
  }
}

/**
 * THE THREE SENTENCES A PLAYER READS, and the keys they are recorded under.
 *
 * Declared here so the SIGN-UP FORM and the ROW IN THE DATABASE cannot drift: the
 * screen renders these, and the registration route stores the same string beside
 * the declaration it belongs to. A form reworded next year must not retroactively
 * change what somebody was asked, which is why the sentence is stored with the
 * row rather than looked up from this file when it is read back.
 *
 * The wording of the second is PAGCOR's own, near enough verbatim — see the note
 * on `isNotGovernmentOrUniformed`. It is long because the rule is.
 */
/**
 * WHAT A PLAYER DOES, AND WHERE THEIR MONEY COMES FROM.
 *
 * PAGCOR form OMD/Online-0060 Rev. 2, item 21 fields 7 and 8. Two of the ten
 * mandatory fields, and two of only three that CANNOT come off a document — no ID
 * card states your occupation or your source of funds, and CIRA's fifteen
 * extracted fields contain neither. They are the player's own declaration.
 *
 * ── CLOSED SETS, BECAUSE THESE ARE AML FIELDS FIRST ───────────────────────
 *
 * Item 16 requires AMLC-compliant funding, and "what does this person do, and
 * where does their money come from" is the question behind every source-of-funds
 * rule. Free text cannot be screened, grouped, or compared against a deposit
 * pattern. A closed set can.
 *
 * ── NO GOVERNMENT OPTION, DELIBERATELY ────────────────────────────────────
 *
 * Item 12 requires every player to declare they are NOT a government official or
 * employee, nor a member of the AFP or the PNP. Offering it here would let
 * somebody contradict, on the same form, a declaration the licence requires them
 * to make — and the platform would have recorded both and believed neither.
 */
export const NATURE_OF_WORK = [
  { key: 'EMPLOYED_PRIVATE', label: 'Employed (private sector)' },
  { key: 'SELF_EMPLOYED', label: 'Self-employed or freelance' },
  { key: 'BUSINESS_OWNER', label: 'Business owner' },
  { key: 'PROFESSIONAL', label: 'Licensed professional' },
  { key: 'OVERSEAS_WORKER', label: 'Overseas Filipino worker' },
  { key: 'RETIRED', label: 'Retired' },
  { key: 'STUDENT', label: 'Student' },
  { key: 'UNEMPLOYED', label: 'Not currently working' },
  { key: 'OTHER', label: 'Something else' },
] as const;

export const SOURCE_OF_INCOME = [
  { key: 'SALARY', label: 'Salary or wages' },
  { key: 'BUSINESS', label: 'Business income' },
  { key: 'INVESTMENTS', label: 'Investments' },
  { key: 'REMITTANCE', label: 'Remittance from family' },
  { key: 'PENSION', label: 'Pension' },
  { key: 'SAVINGS', label: 'Savings' },
  { key: 'INHERITANCE', label: 'Inheritance' },
  { key: 'OTHER', label: 'Something else' },
] as const;

/**
 * The work categories that imply there is an employer or a business to name.
 *
 * The AMLC's minimum customer identification list under RA 10927 asks for "name
 * of employer or business" — the one field either regulator wants that this
 * platform had no column for until sql/995. It is a NAME, which is what a
 * sanctions or PEP screen is actually run against; `natureOfWork` is a closed
 * category, which cannot be.
 *
 * WHY NOT ALL NINE. A retired person, a student and somebody not currently
 * working have neither an employer nor a business, and `sourceOfIncome` already
 * answers where their money comes from — PENSION, SAVINGS, REMITTANCE. Demanding
 * a name from them produces "N/A" typed into a field a reviewer then has to
 * interpret.
 *
 * OTHER is absent too: the detail beside it already says what the person does in
 * their own words, and asking for both is asking the same question twice.
 *
 * A value sent for one of the other four is ACCEPTED and stored — a recently
 * retired person naming their former employer is a coherent answer. Only the
 * DEMAND is narrowed, which is the difference between this and the `*Detail`
 * fields, where a value without an OTHER is a contradiction.
 */
export const WORK_IMPLIES_AN_EMPLOYER = [
  'EMPLOYED_PRIVATE',
  'SELF_EMPLOYED',
  'BUSINESS_OWNER',
  'PROFESSIONAL',
  'OVERSEAS_WORKER',
] as const;

/**
 * What to call the field, per answer, so the screen asks the right question.
 *
 * "Employer" to somebody employed and "Business name" to somebody who owns one is
 * not decoration: a business owner asked for their employer types their own name,
 * and a reviewer reading it cannot tell that from an employee's answer.
 */
export const EMPLOYER_LABEL: Readonly<Record<string, string>> = Object.freeze({
  EMPLOYED_PRIVATE: 'Employer',
  SELF_EMPLOYED: 'Business or trading name',
  BUSINESS_OWNER: 'Business name',
  PROFESSIONAL: 'Practice or employer',
  OVERSEAS_WORKER: 'Employer abroad',
});

export const natureOfWork = z.enum(NATURE_OF_WORK.map((o) => o.key) as [string, ...string[]]);
export const sourceOfIncome = z.enum(SOURCE_OF_INCOME.map((o) => o.key) as [string, ...string[]]);

export const REGISTRATION_DECLARATIONS: readonly {
  /** The field on the request. */
  readonly field: 'isOfLegalAge' | 'isNotGovernmentOrUniformed' | 'isNotGelHolder';
  /** `player.registration_declaration.declaration` — the closed set in sql/980. */
  readonly key: 'AGE_21_OR_OVER' | 'NOT_GOVERNMENT_OR_UNIFORMED' | 'NOT_GEL_HOLDER';
  readonly statement: string;
}[] = [
  {
    field: 'isOfLegalAge',
    key: 'AGE_21_OR_OVER',
    statement: 'I am at least 21 years old.',
  },
  {
    field: 'isNotGovernmentOrUniformed',
    key: 'NOT_GOVERNMENT_OR_UNIFORMED',
    statement:
      'I am not a government official, or an employee connected directly with the operation of ' +
      'the Government or any of its agencies, nor a member of the Armed Forces of the ' +
      'Philippines — including the Army, Navy and Air Force — or the Philippine National Police.',
  },
  {
    field: 'isNotGelHolder',
    key: 'NOT_GEL_HOLDER',
    statement: 'I do not hold a Gaming Employment Licence (GEL).',
  },
];

export const registerPlayerRequest = z.object({
  email: z.email('Enter a valid email address.').max(320),
  displayName: playerDisplayName,
  /* THE NAME AS THE ID PRINTS IT: the name group's six answers (`declaredNameFields`, above), asked
     by its rules in the refinement below. Kept apart from the display name, in
     `player.declared_name` (sql/1018), which nobody but the platform itself can read, and never
     put into the audit row. */
  ...declaredNameFields,
  password: z
    .string()
    .min(
      PLAYER_PASSWORD_MINIMUM,
      `Choose at least ${PLAYER_PASSWORD_MINIMUM} characters — this account holds money you can withdraw.`,
    )
    .max(1024),
  /**
   * Required, and the order matters.
   *
   * The age gate runs BEFORE an account exists, not before the first deposit.
   * Deferring it is the mistake this route is written to avoid: it produces a
   * population of accounts whose age nobody has ever asserted, and every one of
   * them then has to be chased. `player.player.date_of_birth` had no writer at
   * all before this route, which is exactly what "deferred" looks like after a
   * year.
   */
  dateOfBirth: calendarDate,
  /**
   * WHERE THEY LIVE, and it is required for the same reason the age gate is.
   *
   * PAGCOR's offshore regime permits serving players outside the Philippines and
   * PROHIBITS Philippine residents; the domestic PIGO regime is the reverse. The
   * platform could not have enforced either, or reported on it, because
   * `player.player` recorded no residence at all. Asking at registration costs a
   * field; asking afterwards means putting a question to people who have already
   * deposited, whose answer decides whether they were ever allowed an account.
   *
   * Residence, NOT nationality — both regimes key on where a person is, not on
   * what passport they hold. Nationality belongs with the KYC documents.
   *
   * Whether a given answer is ACCEPTED is a licence question and lives in
   * `platform.setting`, evaluated by `player.residency_permitted`. This field
   * only records the answer.
   */
  residenceCountry,
  /**
   * THE THREE DECLARATIONS PAGCOR REQUIRES, EACH ON ITS OWN.
   *
   * Form OMD/Online-0060 Rev. 2, item 12.0 (RF: p. 32 - V.1.o.ii): "For online
   * registration, the OGP shall have a functionality where prospective players
   * shall be asked to declare and confirm the following" — age, not a government
   * or uniformed-service employee, not a GEL holder.
   *
   * ── WHY THREE FIELDS AND NOT ONE ──────────────────────────────────────────
   *
   * There used to be a single `acceptedTerms` boolean behind a checkbox reading
   * "I am 21 or older and accept the terms and privacy policy". Two problems, and
   * the second is the reason this changed:
   *
   *   THE OTHER TWO DECLARATIONS DID NOT EXIST AT ALL. No field, no column, no
   *   question. An account could be opened by a serving police officer or a
   *   gaming-licence holder and the platform had never asked.
   *
   *   AND THE ONE THAT DID EXIST COULD NOT BE EVIDENCED ALONE. Age and terms were
   *   the same boolean, so "show me that this player declared they were 21" had
   *   no better answer than "they ticked a box that also said something else".
   *
   * Each is `z.literal(true)` with its own refusal, so a player who leaves one
   * unticked is told WHICH, and each lands as its own row in
   * `player.registration_declaration` (sql/980) with the sentence they actually
   * read stored beside it.
   *
   * ── THE WORDING IS NOT OURS TO SIMPLIFY ───────────────────────────────────
   *
   * The second declaration is long because the rule is: "not a government
   * official, or employee connected directly with the operation of the Government
   * or any of its agencies, member of the Armed Forces of the Philippines,
   * including the Army, Navy, Air Force, or the Philippine National Police." A
   * friendlier paraphrase would be a different declaration from the one the
   * licence names, and the player would have confirmed something else.
   */
  isOfLegalAge: z.literal(true, 'You must confirm you are 21 or older to open an account.'),
  isNotGovernmentOrUniformed: z.literal(
    true,
    'You must confirm you are not a government, military or police employee. This is a ' +
      'condition of the operator\'s licence and the account cannot be opened without it.',
  ),
  isNotGelHolder: z.literal(
    true,
    'You must confirm you do not hold a Gaming Employment Licence.',
  ),

  /**
   * ITEM 21 FIELDS 3, 7 AND 8 — the three of the ten that no document can answer.
   *
   * The phone number had a COLUMN AND NO WRITER until now: `player.player.phone`
   * existed since the schema was first drawn and nothing had ever put a value in
   * it, so "Contact Information" was an email address and a promise.
   *
   * `*Detail` is demanded only for OTHER, by the refinement below. An OTHER with
   * nothing beside it records that the question was asked and nothing else, which
   * is worse than not asking: a source-of-funds review would read "Other" against
   * somebody depositing a million pesos a month and have learned nothing.
   */
  phone: playerPhone,
  natureOfWork: natureOfWork,
  natureOfWorkDetail: z.string().trim().max(200).optional(),
  sourceOfIncome: sourceOfIncome,
  sourceOfIncomeDetail: z.string().trim().max(200).optional(),

  /**
   * Name of employer or business — the AMLC's tenth field.
   *
   * Demanded by the refinement below only for the five work categories that imply
   * one. Optional in the SHAPE because the other four legitimately have none, and
   * because a conditional requirement is a cross-field rule that a single field's
   * schema cannot express.
   */
  employerOrBusiness: z.string().trim().min(2).max(160).optional(),

  /**
   * Terms acceptance, now SEPARATE from the age declaration.
   *
   * It is its own thing: item 11 requires the Terms of Use be read and accepted at
   * registration, and item 12 requires three declarations. Bundling them meant
   * neither could be produced on its own.
   */
  acceptedTerms: z.literal(true, 'You must accept the terms and privacy policy to continue.'),
})
  .superRefine((body, ctx) => {
    /* The name group, by its own rules (`askDeclaredName`, above). */
    askDeclaredName(body, ctx);

    /* OTHER has to say what. The database states the same rule
       (`player_other_work_is_explained`, sql/991); this refuses first so the
       player is told beside the field rather than by a constraint name. */
    for (const [choice, detail, path] of [
      [body.natureOfWork, body.natureOfWorkDetail, 'natureOfWorkDetail'],
      [body.sourceOfIncome, body.sourceOfIncomeDetail, 'sourceOfIncomeDetail'],
    ] as const) {
      if (choice === 'OTHER' && (detail?.trim().length ?? 0) < 3) {
        ctx.addIssue({
          code: 'custom',
          path: [path],
          message: 'Tell us a little more — a few words is enough.',
        });
      }
      if (choice !== 'OTHER' && detail && detail.trim() !== '') {
        ctx.addIssue({
          code: 'custom',
          path: [path],
          message: 'This only applies when you choose “Something else”.',
        });
      }
    }

    /*
     * AND AN EMPLOYER WHERE THE WORK IMPLIES ONE.
     *
     * Enforced HERE and not in the database, deliberately: 2,060 accounts predate
     * the column and a conditional NOT NULL reaching back over that roster could
     * only be satisfied by inventing employers. sql/995 states the reasoning and
     * constrains only the shape of a value that IS present.
     *
     * So this is the rule, at the one point where a person is answering — and the
     * message names the field the way the screen does, because "employerOrBusiness
     * is required" is not a sentence anybody should be shown.
     */
    if (
      (WORK_IMPLIES_AN_EMPLOYER as readonly string[]).includes(body.natureOfWork) &&
      (body.employerOrBusiness?.trim().length ?? 0) < 2
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['employerOrBusiness'],
        message: `Enter your ${(EMPLOYER_LABEL[body.natureOfWork] ?? 'employer').toLowerCase()}.`,
      });
    }
  });
export type RegisterPlayerRequest = z.infer<typeof registerPlayerRequest>;

/**
 * What registration returns.
 *
 * `verification` carries the OUTBOX's own verdict, never a claim that a message
 * was delivered. Nothing in `services/api` ever sends: it records inside the
 * transaction and `services/worker` dispatches. In development the platform
 * runs `MAIL_OUTBOUND_MODE=SUPPRESS`, so the honest status there is
 * `SUPPRESSED` — and the screen reads this field and says so, rather than
 * printing "check your inbox" over a message that was never going to arrive.
 */
export const registerPlayerResult = z.object({
  playerRef: z.string(),
  displayName: z.string(),
  brandName: z.string(),
  /** Signed in as part of registering — there is no second step to ask for. */
  status: z.literal('SIGNED_IN'),
  verification: z.object({
    messageRef: z.string(),
    /** PENDING · SENT · SUPPRESSED · FAILED · DEAD — the outbox's word, verbatim. */
    status: z.string(),
    to: z.string(),
  }),
});
export type RegisterPlayerResult = z.infer<typeof registerPlayerResult>;

/* ── Sign-in ───────────────────────────────────────────────────────────────── */

/**
 * NOT a re-export of `signInRequest`.
 *
 * That schema carries `trustDevice`, which is a back-office affordance tied to
 * `identity.device_trust` and to a rule about which roles may skip a second
 * factor. A player realm that accepted the field would be a player realm where
 * somebody eventually implements it — and "trust this device" on a consumer
 * product on shared phones is a decision nobody here has made.
 *
 * The zod error messages are also different on purpose: `signInRequest` says
 * "Enter a valid work email address", which is the wrong sentence to show a
 * member of the public.
 */
export const playerSignInRequest = z.object({
  email: z.email('Enter a valid email address.').max(320),
  password: z.string().min(1, 'Enter your password.').max(1024),
});
export type PlayerSignInRequest = z.infer<typeof playerSignInRequest>;

/** The signed-in player, as every player screen needs them. */
export const playerSession = z.object({
  ref: z.string(),
  displayName: z.string(),
  email: z.email(),
  brandName: z.string(),
  /** ACTIVE · FLAGGED · RESTRICTED · SUSPENDED */
  status: z.string(),
  /** UNVERIFIED · PENDING · VERIFIED · REJECTED */
  kycStatus: z.string(),
  tier: z.string(),
  emailVerified: z.boolean(),
  /**
   * Spendable cash, in centavos, as a string.
   *
   * A string because JSON numbers are IEEE 754 doubles and money is not. Cash
   * only — bonus and locked funds are separate accounts and are deliberately
   * excluded, because `wallet.player_cash_balance` is the same definition the
   * withdrawal guard enforces and two definitions of "balance" is how a screen
   * and a payout rail come to disagree.
   */
  cashBalance: z.string(),
  registeredAt: z.iso.datetime(),
  expiresAt: z.iso.datetime(),
});
export type PlayerSession = z.infer<typeof playerSession>;

/* ── Confirming an address ─────────────────────────────────────────────────── */

/**
 * The token arrives in a POST BODY, never in a query string.
 *
 * The link puts it in the URL FRAGMENT — `…/verify-email#token=…` — which
 * browsers do not transmit, so it cannot reach an access log, a proxy log or a
 * `Referer` header on the way to some third party's analytics. The page reads
 * the fragment and posts it. Exactly the shape both invite flows already use.
 */
export const verifyPlayerEmailRequest = z.object({
  token: z.string().min(20, 'That confirmation link is incomplete.').max(200),
});
export type VerifyPlayerEmailRequest = z.infer<typeof verifyPlayerEmailRequest>;

/* ── Self-service password reset ───────────────────────────────────────────
   The thing the note at the head of this file used to say was deliberately
   absent. It was absent because the platform could not send an email, and half
   a credential-recovery path is worse than none. `packages/mailer` is built and
   wired, so the blocker is gone and the sentence has stopped being true — see
   `services/api/src/modules/player-auth/password-reset.ts` for the whole
   design, and the note at the head of that file for what it still does not do.
   ────────────────────────────────────────────────────────────────────────── */

/**
 * How long a reset link lives, in MINUTES.
 *
 * NOT `INVITE_VALID_HOURS`, and this is the one link on the platform that is
 * deliberately shorter than the other three. An invite goes to somebody with no
 * account: the worst a stale one can do is fail to open one. A reset link takes
 * over an account that already exists and already holds withdrawable money, and
 * it lives in an inbox — the single place a credential is most likely to be
 * read by somebody other than its owner, on a shared phone, in a forwarded
 * thread, or in a mailbox whose own password went in the same breach.
 *
 * Sixty minutes is long enough to walk to a laptop and short enough that a
 * message found later is not a key. Stated in minutes rather than as a fraction
 * of an hour so nothing downstream has to round it.
 */
export const PLAYER_PASSWORD_RESET_VALID_MINUTES = 60;

/**
 * Asking for a link.
 *
 * The address, and NOTHING else. In particular no `brandId` — brand is resolved
 * server-side from the request host, exactly as it is for registration and
 * sign-in, and for the same reason: a tenancy boundary a caller can name is not
 * a boundary. A `brandId` here would let a stranger ask for a reset against any
 * operator's roster from any host.
 */
export const requestPlayerPasswordResetRequest = z.object({
  email: z.email('Enter a valid email address.').max(320),
});
export type RequestPlayerPasswordResetRequest = z.infer<typeof requestPlayerPasswordResetRequest>;

/**
 * What asking for a link returns — AND WHAT IT MUST NOT.
 *
 * THIS SHAPE IS THE ENUMERATION DEFENCE, so it is worth saying what is missing
 * and why. Registration deliberately DOES leak whether an address is taken, and
 * the note on that route explains the trade it is making: somebody who mistyped
 * their own address must not be left with no account and no error. A reset
 * route has no such excuse — the person who typed the address either has an
 * account or does not, and telling a STRANGER which is a list of who to point a
 * password file at.
 *
 * So there is no `found`, no `playerRef`, no `displayName`, no `messageRef` and
 * no per-message status. Every field below has the same value for an address
 * with an account, an address without one, and an address whose account is
 * suspended. The route answers identically in body AND in elapsed time, and
 * `services/api/src/__tests__/player-password-reset.integration.test.ts`
 * compares both, byte for byte and median against median.
 *
 * `mailDelivery` is the one honest thing that CAN be said, and it is a property
 * of the DEPLOYMENT rather than of the account: it is computed from
 * `MAIL_OUTBOUND_MODE` and `MAIL_ALLOWLIST` against the address that was typed,
 * which returns the same answer whether or not a player exists behind it. It is
 * here because development runs `MAIL_OUTBOUND_MODE=SUPPRESS` and a screen that
 * printed "check your inbox" over an environment that sends no mail at all
 * would be telling somebody to wait for something that was never coming. It is
 * NOT the outbox's verdict on a particular message — that would leak, because a
 * message only exists when the account does.
 */
export const requestPlayerPasswordResetResult = z.object({
  /** The only status there is. It does not mean a message was recorded. */
  status: z.literal('RECEIVED'),
  /**
   * ENQUEUED · SUPPRESSED — what this environment does with outbound mail for
   * this address, from configuration alone.
   */
  mailDelivery: z.enum(['ENQUEUED', 'SUPPRESSED']),
});
export type RequestPlayerPasswordResetResult = z.infer<typeof requestPlayerPasswordResetResult>;

/**
 * Spending the link.
 *
 * The token arrives in a POST BODY and never in a query string, for the reason
 * `verifyPlayerEmailRequest` states above: the link puts it in the URL
 * FRAGMENT, which browsers do not transmit, so it cannot reach an access log, a
 * proxy log or a `Referer` header. This one matters more than the verification
 * link does — that token confirms an address, this one takes over an account.
 */
export const completePlayerPasswordResetRequest = z.object({
  token: z.string().min(20, 'That reset link is incomplete.').max(200),
  password: z
    .string()
    .min(
      PLAYER_PASSWORD_MINIMUM,
      `Choose at least ${PLAYER_PASSWORD_MINIMUM} characters — this account holds money you can withdraw.`,
    )
    .max(1024),
});
export type CompletePlayerPasswordResetRequest = z.infer<
  typeof completePlayerPasswordResetRequest
>;

/**
 * What completing a reset returns.
 *
 * `sessionsEnded` is reported rather than assumed, and the screen prints it.
 * The reset bumps `token_version`, which stops every cookie minted before it at
 * its holder's next request — including whoever's cookie caused the reset to be
 * needed. A screen that said "password changed" without saying that would leave
 * the one fact the player most needs unsaid, and a player signed in on a phone
 * would read being logged out as a fault.
 *
 * NO SESSION IS MINTED. Holding the link proves control of the inbox; signing
 * in is a separate act with its own attempt row, and minting a session here
 * would skip the record that trail is made of. The same decision the partner
 * acceptance route makes, and for the same reason.
 */
export const completePlayerPasswordResetResult = z.object({
  status: z.literal('RESET'),
  displayName: z.string(),
  playerRef: z.string(),
  /** How many live sessions this ended. Zero is a real and common answer. */
  sessionsEnded: z.number().int().nonnegative(),
  signInRequired: z.literal(true),
});
export type CompletePlayerPasswordResetResult = z.infer<
  typeof completePlayerPasswordResetResult
>;

/* ── Changing a password from inside a session ─────────────────────────────── */

/**
 * The change a signed-in player makes themselves.
 *
 * ── WHY IT NEEDED A SHAPE OF ITS OWN ───────────────────────────────────────
 *
 * Until this landed there was no route that changed a player's password at all,
 * and the security screen said so: to change one, sign out and use the reset
 * link. That is a credential-recovery path standing in for a routine act, and
 * it fails exactly where it matters — a player who thinks their password has
 * leaked has to sign THEMSELVES out and wait on an inbox, and a player who has
 * also lost the inbox cannot change it at all.
 *
 * ── THE CURRENT PASSWORD IS REQUIRED, AND IT IS NOT CEREMONY ────────────────
 *
 * A live session is not proof of the person: it is proof of a cookie. Somebody
 * who has stolen one could otherwise set a new password, and the account is
 * theirs — the real owner's own password stops working and every recovery path
 * now runs through a mailbox the attacker can also reset. Asking for the
 * current password is what makes the change require something the session alone
 * does not carry, and it is the same asymmetry `two-factor.ts` describes: each
 * route asks for the thing the attacker of that route does not have.
 *
 * ── AND NOT THE AUTHENTICATOR ──────────────────────────────────────────────
 *
 * Deliberately not a TOTP code as well. The second factor on this platform
 * guards MONEY LEAVING, and requiring it here would mean a player who lost
 * their phone cannot change a leaked password — which turns a security control
 * into the reason the account stays compromised.
 */
export const changePlayerPasswordRequest = z.object({
  currentPassword: z.string().min(1, 'Enter your current password.').max(1024),
  newPassword: z
    .string()
    .min(
      PLAYER_PASSWORD_MINIMUM,
      `Choose at least ${PLAYER_PASSWORD_MINIMUM} characters — this account holds money you can withdraw.`,
    )
    .max(1024),
});
export type ChangePlayerPasswordRequest = z.infer<typeof changePlayerPasswordRequest>;
